import { useEffect, useState } from 'react';
import { Typography, type TypographyProps } from '../ui';
import { stageLine, threadStage, withGroupLine, type StageThread } from '../../../lib/outreach-stage';
import { renderStageLine, renderWithGroupLine } from '../../../lib/outreach-stage-render';
import { refetchStagesAtZero } from '../../../lib/contact-book';
import { useAgentName } from '../../../lib/use-agent-name';
import { useTranslation } from '../../../lib/i18n';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * The instant a stage line is drawn against, computed fresh on EVERY render (rule 9) — the timers below
 * only decide WHEN a render happens, never what instant it reads.
 *
 * - Every row re-renders at least once a minute, so "2m ago" / "Sends at …" never go stale on screen.
 * - A SCHEDULED send more than an hour out arms a timeout at `sendAt − 1h`, so the row turns into the
 *   countdown when it crosses the hour while on screen.
 * - Within the hour it ticks every second, and a timeout at `sendAt` refetches the stages ONCE
 *   (`refetchStagesAtZero`, deduped per outreach + send time at the query layer). A row that mounts with
 *   the send already past refetches through the same deduped call.
 * Every timer is owned by its effect and cleared on every exit (unmount, the thread changing).
 */
export function useStageNow(thread: StageThread): Date {
  const [, setTick] = useState(0);
  const now = new Date();
  const sendAt = threadStage(thread) === 'scheduled' ? thread.heldSend?.sendAt : undefined;
  const sendMs = sendAt ? Date.parse(sendAt) : NaN;
  const ahead = Number.isFinite(sendMs) ? sendMs - now.getTime() : NaN;
  const phase = !Number.isFinite(ahead) ? 'none' : ahead > HOUR_MS ? 'far' : ahead > 0 ? 'ticking' : 'passed';
  const outreachId = thread.outreachId;

  useEffect(() => {
    const rerender = () => setTick((n) => n + 1);
    const timers: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];
    if (phase === 'ticking') {
      intervals.push(setInterval(rerender, 1000));
      timers.push(
        setTimeout(() => {
          rerender();
          if (sendAt) refetchStagesAtZero(outreachId, sendAt);
        }, Math.max(0, sendMs - Date.now())),
      );
    } else {
      intervals.push(setInterval(rerender, MINUTE_MS));
      if (phase === 'far') timers.push(setTimeout(rerender, Math.max(0, sendMs - HOUR_MS - Date.now())));
      if (phase === 'passed' && sendAt) refetchStagesAtZero(outreachId, sendAt);
    }
    return () => {
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [phase, sendAt, sendMs, outreachId]);

  return now;
}

/**
 * One thread's stage line, rendered — the row line on the Inbox / contact rows, and (with `withGroup`) the
 * contact screen's "{group} · {line}". The shape comes from `lib/outreach-stage.ts`, the strings from
 * `lib/outreach-stage-render.ts`; this binds them to `t`, the agent's name and the countdown clock.
 */
export function StageLine({
  thread,
  contactName,
  withGroup = false,
  variant = 'p3',
  color = 'secondary',
  className,
}: {
  thread: StageThread;
  contactName: string;
  withGroup?: boolean;
  variant?: TypographyProps['variant'];
  color?: TypographyProps['color'];
  className?: string;
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const now = useStageNow(thread);
  const ctx = { t: t as (key: string, params?: Record<string, unknown>) => string, maestroAgentName: agent.name, now };
  const textValue = withGroup
    ? renderWithGroupLine(withGroupLine(thread, contactName, now), ctx)
    : renderStageLine(stageLine(thread, contactName, now), ctx);
  if (!textValue) return null;
  return (
    <Typography variant={variant} color={color} className={className} dir="auto">
      {textValue}
    </Typography>
  );
}
